import {
    getNotifications,
    updateNotification,
    deleteNotification,
} from "./userNotificationService"
import useAxiosPrivate from "../../hooks/useAxiosPrivate"
import { useAuth } from "../../contexts/AuthContext"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

export default function useNotifications() {
    const { auth } = useAuth()
    const axiosPrivate = useAxiosPrivate()
    const queryClient = useQueryClient()

    // Invalidate notifications when a push is received.
    // BroadcastChannel covers the normal path; the serviceWorker message
    // listener covers iOS cold-start where BroadcastChannel delivery is
    // not guaranteed before the page listener is ready.
    useEffect(() => {
        if (!auth?.token) return
        const channel = new BroadcastChannel("notifications")
        channel.onmessage = (event) => {
            if (event.data?.type === "NOTIFICATION_RECEIVED") {
                queryClient.invalidateQueries(["getNotifications"])
            }
        }

        function handleSWMessage(event) {
            if (event.data?.type === "NOTIFICATION_RECEIVED") {
                queryClient.invalidateQueries(["getNotifications"])
            }
        }
        navigator.serviceWorker?.addEventListener("message", handleSWMessage)

        return () => {
            channel.close()
            navigator.serviceWorker?.removeEventListener(
                "message",
                handleSWMessage
            )
        }
    }, [auth?.token, queryClient])

    const {
        isPending,
        isError,
        data: notificationsData,
        error,
        refetch,
    } = useQuery({
        queryKey: ["getNotifications"],
        queryFn: () => getNotifications({ axiosPrivate, token: auth?.token }),
        enabled: !!auth?.token,
    })
    const notifications = notificationsData?.data?.notifications || []
    const unreadCount = notifications.filter((n) => !n.read_status).length

    const markAsReadMutation = useMutation({
        mutationFn: ({ notification_object_id }) =>
            updateNotification({
                axiosPrivate,
                token: auth?.token,
                notification_object_id,
                read_status: true,
            }),
        onMutate: async ({ notification_object_id }) => {
            await queryClient.cancelQueries(["getNotifications"])
            const previous = queryClient.getQueryData(["getNotifications"])
            queryClient.setQueryData(["getNotifications"], (old) => {
                if (!old?.data?.notifications) return old
                return {
                    ...old,
                    data: {
                        ...old.data,
                        notifications: old.data.notifications.map((n) =>
                            n.notification_object_id === notification_object_id
                                ? { ...n, read_status: true }
                                : n
                        ),
                    },
                }
            })
            return { previous }
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(["getNotifications"], context.previous)
        },
        onSettled: () => {
            queryClient.invalidateQueries(["getNotifications"])
        },
    })

    const deleteNotificationMutation = useMutation({
        mutationFn: ({ notification_object_id }) =>
            deleteNotification({
                axiosPrivate,
                token: auth?.token,
                notification_object_id,
            }),
        onMutate: async ({ notification_object_id }) => {
            await queryClient.cancelQueries(["getNotifications"])
            const previous = queryClient.getQueryData(["getNotifications"])
            queryClient.setQueryData(["getNotifications"], (old) => {
                if (!old?.data?.notifications) return old
                return {
                    ...old,
                    data: {
                        ...old.data,
                        notifications: old.data.notifications.filter(
                            (n) =>
                                n.notification_object_id !==
                                notification_object_id
                        ),
                    },
                }
            })
            return { previous }
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(["getNotifications"], context.previous)
        },
        onSettled: () => {
            queryClient.invalidateQueries(["getNotifications"])
        },
    })

    return {
        notifications,
        unreadCount,
        isPending,
        isError,
        error,
        refetch,
        markAsRead: markAsReadMutation.mutate,
        deleteNotification: deleteNotificationMutation.mutate,
    }
}
